import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../DashboardLayout';
import NavigationMenu from '../NavigationMenu';
import ExcelReportExport from './ExcelReportExport';
import * as emailApi from '../../services/emailMarketingApi';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Mail, Eye, MousePointer } from 'lucide-react';

const EmailReportPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await emailApi.getStats();
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Check if user has access to reports
  useEffect(() => {
    if (user && !['admin', 'superadmin', 'pdcare', 'campaignadmin', 'campaignreport'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const StatCard = ({ label, value, icon: Icon, color }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value?.toLocaleString() || '0'}</p>
        </div>
        <div className={`p-2 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={20} color={color} />
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout title="Email Reports" menu={<NavigationMenu />}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Email Marketing Reports</h1>
          <p className="text-sm text-gray-500 mt-1">
            Export and analyze email campaign performance data
          </p>
        </div>

        {/* Stats Grid */}
        {!loading && !error && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total Campaigns"
              value={stats.total?.campaigns}
              icon={BarChart2}
              color="#2563eb"
            />
            <StatCard
              label="Emails Sent"
              value={stats.total?.sent}
              icon={Mail}
              color="#059669"
            />
            <StatCard
              label="Unique Opens"
              value={stats.total?.unique_opens}
              icon={Eye}
              color="#7c3aed"
            />
            <StatCard
              label="Unique Clicks"
              value={stats.total?.unique_clicks}
              icon={MousePointer}
              color="#ea580c"
            />
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Export Email Logs</h2>
            <p className="text-sm text-gray-500 mt-1">
              Select a promotion to filter and download detailed email logs
            </p>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : (
              <ExcelReportExport 
                stats={stats}
                onExportStart={() => console.log('Export started')}
                onExportEnd={() => console.log('Export ended')}
              />
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">📊 Summary Stats</h3>
            <p className="text-sm text-blue-700">
              Overview of campaign performance including total emails, opens, and clicks
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">📧 Detailed Logs</h3>
            <p className="text-sm text-green-700">
              Individual email records with delivery status, opens, clicks, and timestamps
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-medium text-purple-900 mb-2">🎯 Promotion Filter</h3>
            <p className="text-sm text-purple-700">
              Filter logs by promotion to analyze specific campaign performance
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmailReportPage;